import { DeleteOutline } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import {
  Avatar,
  Chip,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { createTheme, styled, Theme, ThemeProvider } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridApiPro,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridRowSelectionModel,
  GridTreeNodeWithRender,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import _, { debounce, isEmpty } from 'lodash';
import React, {
  createContext,
  forwardRef,
  isValidElement,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as CheckIcon } from '../../../src/assests/svg/check.svg';
import { ReactComponent as DropDownIcon } from '../../../src/assests/svg/drop_down.svg';
import { ReactComponent as DropUpIcon } from '../../../src/assests/svg/drop_up.svg';
import {
  downloadExportDocument,
  exportDocumentList,
  fetchSummary,
  GenerateSummary,
  getAllDocResults,
  getEcaFields,
  getSearchResults,
} from '../../api/EcaServices';
import GenerateSummarySVG from '../../assests/svg/generate-summary.svg';
import NlpSummarySVG from '../../assests/svg/summary-lock.svg';
import ShowSummarySVG from '../../assests/svg/summary.svg';
import {
  DELAY_MS,
  LOAD_DOC_TYPE,
  MAX_ATTEMPTS,
  POPUP_BUTTON_STYLE,
  SEARCH_SCOPE_TYPE,
  SSE_ENDPOINT_ECA,
  SSE_SERVICE_TYPE,
  SUMMARY_FILE_TYPE,
} from '../../constants/constants';
import { useToast } from '../../contexts/toastContext/ToastContext';
import {
  setPaginationDefaultLoad,
  setSearchLoader,
  setSearchResultError,
} from '../../redux/slices/ecaAdhocSearchSlice';
import {
  getColumnsList,
  getNlpColumnsList,
  ISortOrder,
  removeConfidenceColumn,
  resetDocListForContext,
  setColumnsList,
  setFilterList,
  setSortOrder,
} from '../../redux/slices/ecaDocListSlice';
import {
  setAllGridDocuments,
  setCreateSummaryAllowed,
  setEcaDocumentViewerModal,
  setEcaDocZoom,
  setStrSearchFlag,
  setUpdateAllGridDocuments,
} from '../../redux/slices/ecaDocumentViewerSlice';
import { RootState } from '../../redux/store';
import {
  downloadResponseAsCSVInBlob,
  formateNumber,
  waitBeforeRetry,
} from '../../utils/utils';
import AurButton from '../common/aur-button/AurButton';
import AurCheckbox from '../common/aur-checkbox/AurCheckbox';
import AurIcon, { IconColors, IconNames } from '../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import AurMenu from '../common/aur-menu/AurMenu';
import AurPaginationPro from '../common/aur-pagination-pro/AurPaginationPro';
import AurPopup from '../common/aur-popup/AurPopup';
import { SavedSearchDetails } from '../eca/SavedSearchPanel';
import { IFieldModal } from '../pages/field-management/custom-field/constant';
import BulkTaggingModal, {
  TaggingFLowType,
} from './bulk-tagging/BulkTaggingModal';
import ConfidenceScore from './ConfidenceScore';
import CustomDocListFilters from './CustomDocListFilters';
import CustomFilterInput from './CustomFilterInput';
import { CustomGridFilterPanel } from './CustomFilterPanel';
import {
  ArrayItemType,
  getDefaultDocListColumns,
  DetailPanelContentProps,
  docListResponseType,
  FAMILY_STATUS,
  FileTypeToIconMap,
  fileTypeToIconMap,
  FormattedItem,
  IDocListExportReqBody,
  IDocListReqBody,
  IDocListResponse,
  IFilterSet,
  IGridColDef,
} from './eca-doc-list.util';
import EcaDocumentContainer from './eca-document-viewer/EcaDocumentContainer';
import EcaSaveSearch from './eca-save-search/EcaSaveSearch';
import FieldsSortDrawer from './manage-fields-sort/FieldsSortDrawer';
import AurEcaSearchLoader from '../common/aur-eca-search-loader/AurEcaSearchLoader';
import { IRunSearchError } from '../pages/index-search/IndexSearchPage';
import { showErrorToast } from '../../api/utils';
import { AurLightTooltip } from '../common/aur-light-tooltip/aur-light-tooltip';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../hooks/permissions/usePermissionsByPage';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
  GA_ERROR_TYPES,
  GA_FILE_EXTENTIONS,
} from '../../constants/analyticsConstants';
import { countRules } from '../pages/index-search/query-field/query-field-utils';
import { GridFilterPanelProps } from '@mui/x-data-grid/components/panel/filterPanel/GridFilterPanel';

const SelectedItems = { allInDocument: 'allInDocument', allInPage: 'allInPage' } as const;
type SelectedItemsTypes = typeof SelectedItems[keyof typeof SelectedItems];
const dropdownMenuOptions = [
  { key: SelectedItems.allInDocument, value: 'All in Document List' },
  { key: SelectedItems.allInPage, value: 'All in page' },
];

export const defaultFilterModel = {
  items: [],
  logicOperator: GridLogicOperator.Or,
};

export const defaultFilterSet: IFilterSet = {
  linkOperator: 'AND',
  items: [],
};

// Will remove once implement tooltip for whole data grid and pagination
const LightTooltip = styled(({ className, ...props }: TooltipProps) =>
  props?.title ? (
    <Tooltip
      {...props}
      classes={{ popper: className, arrow: 'custom-arrow' }}
      placement="top"
      arrow
      disableInteractive
    />
  ) : null
)(({ theme }: { theme: Theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#000000de',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    marginTop: -11,
  },
}));

const DetailPanelContent = (props: DetailPanelContentProps) => {
  const {
    row: { nlp_summary_text, id, nlpNotEnabled },
    handleRowExpansion,
  } = props;
  return (
    <>
      {
        <Box className="summary-container">
          <Box>
            <Typography className="summary-header" tabIndex={0}>
              {nlpNotEnabled
                ? t('ecaDocList.nlpNotEnabledHeader')
                : t('ecaDocList.summary')}
            </Typography>
            <Typography className="summary-text" tabIndex={0}>
              {nlpNotEnabled
                ? t('ecaDocList.nlpNotEnabledDesc')
                : nlp_summary_text}
            </Typography>
          </Box>
          <Box
            className="summary-close"
            onClick={() => handleRowExpansion(id)}
            data-testid={`summary-close-${id}`}
            tabIndex={0}
          >
            {t('buttons.close')}
          </Box>
        </Box>
      }
    </>
  );
};

function CustomLoadingOverlay() {
  return (
    <div className="loading-overlay">
      <CircularProgress
        thickness={5}
        size={64}
        aria-label={t('common.loading')}
      />
    </div>
  );
}

const HeaderColumnFilter = ({
  column,
  params,
  currentCol,
  apiRef,
}: {
  column: any;
  params: GridRenderHeaderFilterProps;
  currentCol: React.MutableRefObject<string>;
  apiRef: React.MutableRefObject<GridApiPro>;
}) => {
  return (
    <div
      onClick={() => {
        if (currentCol.current != column.name) {
          currentCol.current = column.name;
        }
      }}
      onFocus={() => {
        if (currentCol.current != column.name) {
          currentCol.current = column.name;
        }
      }}
      onBlur={() => {
        apiRef.current.setColumnHeaderFilterFocus('');
        apiRef.current.setColumnHeaderFocus('');
      }}
    >
      <CustomDocListFilters
        gridRenderHeaderFilterProps={params}
        dataType={column.dataType.toLowerCase()}
        UIDataType={column.UIDataType?.toLowerCase()}
      />
    </div>
  );
};

type CheckboxHeaderMenuProps = {
  onSelectedChange?: (key: SelectedItemsTypes | null) => void;
}

type CheckboxHeaderMenuHandle = {
  resetMenuOption: () => void;
  setSelectedOption: (option: SelectedItemsTypes | null) => void;
}

type SelectionHeaderContextValue = {
  selectedItem: SelectedItemsTypes | null;
  selectionModel: string[];
  rowsLength: number;
  onHeaderCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const SelectionHeaderContext = createContext<SelectionHeaderContextValue>({
  selectedItem: null,
  selectionModel: [],
  rowsLength: 0,
  onHeaderCheckboxChange: () => undefined,
});

const SelectAllHeaderCheckbox = () => {
  const { selectedItem, selectionModel, rowsLength, onHeaderCheckboxChange } =
    useContext(SelectionHeaderContext);

  const checked = selectedItem === SelectedItems.allInDocument;
  const indeterminate =
    selectedItem === SelectedItems.allInPage ||
    (selectionModel.length > 0 && selectionModel.length < rowsLength);

  return (
    <AurCheckbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={onHeaderCheckboxChange}
      data-testid="eca-select-all-header-checkbox"
      className='sr-only'
      inputProps={{ 'aria-label': t('aurCheckbox.checkedCheckbox') }}
    />
  );
};

const CheckboxHeaderMenu = forwardRef<CheckboxHeaderMenuHandle, CheckboxHeaderMenuProps>(({ onSelectedChange }, ref) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOption, setSelectedOption] = useState<SelectedItemsTypes | null>(null);

  const handleDropdownSelect = (key: SelectedItemsTypes) => {
    if (selectedOption == key) {
      setSelectedOption(null);
      onSelectedChange?.(null);
      return;
    }
    setSelectedOption(key);
    onSelectedChange?.(key);
  };

  const { trackGAEvent } = useAnalytics();


  const open = Boolean(anchorEl)

  const dropdownMenu = useMemo(() => {
    return dropdownMenuOptions.map((option) => {
      return {
        label: option.value.toString(),
        selected: selectedOption == option.key as SelectedItemsTypes,
        children: (
          <span className="menu-checkmark">
            <CheckIcon />
          </span>
        ),
        onClick: () => {
          handleDropdownSelect(option.key);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: option.value,
              region: GA_REGIONS.BODY,
              type: GA_CLICK_TYPES.DROPDOWN,
              action:
                option.key == SelectedItems.allInDocument
                  ? t('gaTracking.ecaDocList.selectedAllDocuments')
                  : t(
                    'gaTracking.ecaDocList.selectedAllDocumentsInPage'
                  ),
            },
          });
        },
      };
    })
  }, [selectedOption]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  useImperativeHandle(ref, () => ({
    resetMenuOption: () => {
      setSelectedOption(null);
    },
    setSelectedOption: (option: SelectedItemsTypes | null) => {
      setSelectedOption(option);
    },
  }));

  const onClickMenuButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.currentTarget && !anchorEl) {
      setAnchorEl(event.currentTarget);
    } else {
      handleClose();
    }
  };

  return <>
    <span
      data-testid="end-icon"
      onClick={onClickMenuButton}
      tabIndex={0}
    >
      {!open ? (
        <DropUpIcon className="drop-Down-icon" aria-hidden="true" />
      ) : (
        <DropDownIcon className="drop-Down-icon" aria-hidden="true" />
      )}
    </span>
    <AurMenu
      cssClassName={'menu-item-box'}
      labelClassName={'midnight_1100'}
      anchorElement={anchorEl}
      shouldShow={open}
      onClose={handleClose}
      menuItems={dropdownMenu}
      direction="left"
      aria-labelledby="addUserButton"
    />
  </>

})

// Define the light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
  },
  components: {
    MuiPaper: {
      defaultProps: {
        sx: {
          backgroundColor: '#d3e4ff !important',
          border: '1px solid #a0aec2 !important',
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        sx: {
          color: '#0c1119',
          fontFamily: 'Inter',
          fontWeight: '400',
          fontSize: '14px',
          lineHeight: '16px',
          letterSpacing: '0.5px',
        },
      },
    },
  },
});



const abortControllers: Map<string, AbortController> = new Map();
const EcaDocListGrid = forwardRef((props: {
  rowData?: docListResponseType[];
  searchID?: string;
  docSetId?: string;
  searchTime?: string;
  openSaveSearch?: boolean;
  setOpenSaveSearch?: (data: boolean) => void;
  viewDocType?: string;
  selectedIndex?: string;
  handleGlobalSelectedItem?: (viewDoc: string) => void;
  openDrawer?: (data: boolean) => void;
  isNlpSearch?: boolean;
  nlpIndexId?: number;
  hasSelectedFamilyGroupIdentifier?: boolean;
  apiControllers?: Map<string, AbortController>;
  onSaveSearchClick?: (builderData: any, fieldTempId: string) => void;
  saveSearchLoader?: boolean;
  runSearchError?: IRunSearchError;
  setRunSearchError?: (data: IRunSearchError) => void;
  stdIndexFieldTemplateId?: number;
  onCancelClick?: () => void;
}, ref) => {
  const {
    rowData = [],
    searchID,
    docSetId,
    searchTime,
    openSaveSearch,
    setOpenSaveSearch,
    viewDocType,
    handleGlobalSelectedItem,
    openDrawer,
    isNlpSearch = false,
    nlpIndexId,
    hasSelectedFamilyGroupIdentifier,
    apiControllers,
    onSaveSearchClick,
    saveSearchLoader,
    runSearchError,
    setRunSearchError,
    stdIndexFieldTemplateId,
    onCancelClick,
  } = props;
  const [selectionModel, setSelectionModel] = useState<string[]>([]);
  const [selectionDocuments, setSelectionDocuments] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<SelectedItemsTypes | null>(null);
  const [startIndex, setStartIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(200);
  const [rows, setRows] = useState<docListResponseType[]>(rowData);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [selectedDocLink, setSelectedDocLink] = React.useState<string>('');
  const [scrollFetching, setScrollFetching] = React.useState<boolean>(false);
  const currentCol = React.useRef<string>('');
  const [filterSet, setFilterSet] = useState<IFilterSet>(defaultFilterSet);
  const [filterCount, setFilterCount] = React.useState<number>(0);
  const [clearFilterModal, setClearFilterModal] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [isDisabledSaveSearchBtn, setIsDisabledSaveSearchBtn] =
    useState<boolean>(true);
  const [showSearchLoader, setShowSearchLoader] = useState<boolean>(false);
  const dispatch = useDispatch();

  const [allColumnsDetails, setAllColumnsDetails] = useState<IGridColDef[]>();

  const { index, builder } = useSelector((state: RootState) => state.query);
  const bulkTaggingRef = useRef<HTMLButtonElement>(null);

  const [docSetIdForAllDocsList, setDocSetIdForAllDocsList] =
    useState<string>();

  const [docSetIdForNLPIndex, setDocSetIdForNLPIndex] = useState<string>();

  const { ecaDocumentViewerPopup: ecaDocumentViewer } = useSelector(
    (state: RootState) => state.ecaDocumentViewer
  );

  const { globalSelectedScopeItem } = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const pageColumns = useSelector(isNlpSearch ? getNlpColumnsList : getColumnsList);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const { showToast } = useToast();

  const eventSourceRef = useRef<EventSource | null>(null);
  const { trackGAEvent } = useAnalytics();
  const prevFiltersRef = React.useRef<GridFilterItem[]>([]);
  const filterSourceRef = React.useRef<'HEADER' | 'ADVANCE' | null>(null);
  const checkboxHeaderMenuRef = useRef<CheckboxHeaderMenuHandle | null>(null);
  const trackGAEventRef = useRef(trackGAEvent);
  trackGAEventRef.current = trackGAEvent;
  const selectedItemRef = useRef(selectedItem);
  selectedItemRef.current = selectedItem;
  const handleDropdownSelectRef = useRef<(key: SelectedItemsTypes | null) => void>(() => undefined);
  const handleRowExpansionRef = useRef<(id: string) => void>(() => undefined);
  const handleSummaryGenRef = useRef<
    (params: GridRenderCellParams<docListResponseType, GridTreeNodeWithRender>) => void
  >(() => undefined);

  const {
    hasDocumentPreviewPerm,
    hasTaggingDocumentPerm,
    hasDocumentSummaryGenerationPerm,
    hasSavedSearchesPerm,
    hasSearchesPerm,
  } = usePermissionsByPage(PAGE_NAME.ECA_DOC_LIST);

  useImperativeHandle(ref, () => ({
    resetData: () => {
      setAllDocumentsCount(0);
      setRows([]);
      setIsDisabledSaveSearchBtn(true);
    }
  }));

  const handleonRowSelectionModelChange = useCallback(
    (rowSelectionModel: GridRowSelectionModel) => {
      if (selectedItemRef.current == SelectedItems.allInDocument) {
        const allRowIds = apiRef.current.getAllRowIds();
        setSelectionModel(allRowIds as string[]);
      } else if (selectedItemRef.current == SelectedItems.allInPage) {
        if (rowSelectionModel.length != rows.length) {
          checkboxHeaderMenuRef.current?.resetMenuOption();
          setSelectedItem(null)
        } else {
          setSelectedItem(SelectedItems.allInPage);
        }
        setSelectionModel(rowSelectionModel as string[]);

      } else {
        setSelectionModel(rowSelectionModel as string[]);
      }
    },
    [selectedItem]
  );

  useEffect(() => {
    if (!isNlpSearch) {
      dispatch(removeConfidenceColumn());
    }
  }, [dispatch]);

  const handlePageChange = (page: number) => {
    setStartIndex(page);
    dispatch(setPaginationDefaultLoad(false));
  };
  const [showBulkTaggingModalVisible, setShowBulkTaggingModalVisible] =
    useState<boolean>(false);
  const [showFamilyMembersWarningModal, setShowFamilyMembersWarningModal] =
    useState<boolean>(false);
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const { projectId, nlpSearchEnabled } = useSelector(
    (state: RootState) => state.reports
  );
  const {
    sortOrder,
    nlpSortOrder,
    filterList,
    viewDocName,
    editDocsName,
    queryData,
    docListContext,
  } = useSelector((state: RootState) => state.ecaDocList);
  const { updatedAllGridDocuments } = useSelector(
    (state: RootState) => state.ecaDocumentViewer
  );

  const pageSortOrder = useMemo(
    () => (isNlpSearch ? nlpSortOrder : sortOrder),
    [nlpSortOrder, sortOrder, isNlpSearch]
  );

  const { globalSavedSearchDetails, paginationDefaultLoad } = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const isDocListContextStale =
    viewDocName === LOAD_DOC_TYPE.ALL_DOC &&
    (docListContext?.clientId !== clientId ||
      docListContext?.projectId !== projectId);

  useEffect(() => {
    if (!isDocListContextStale) {
      return;
    }

    setFilterSet(defaultFilterSet);
    dispatch(resetDocListForContext({ clientId, projectId }));
    dispatch(setSearchResultError(null));
  }, [clientId, dispatch, isDocListContextStale, projectId]);

  const savedSearchScope =
    globalSavedSearchDetails?.useAsScope &&
    globalSavedSearchDetails?.scope?.scopeName;

  const runFlag = useRef<boolean>(false);

  useEffect(() => {
    if (editDocsName == LOAD_DOC_TYPE.SAVED_SEARCHES_EDIT) {
      setIsDisabledSaveSearchBtn(false);
      setRows([]);
      setFilterCount(0);
      runFlag.current = false;
    }
    if (
      queryData.dtSyntaxQuery ||
      (queryData.jsonLogicQuery &&
        (queryData?.jsonLogicQuery ?? '').trim().toLowerCase() !== 'false')
    ) {
      setIsDisabledSaveSearchBtn(false);
    } else if (viewDocName == LOAD_DOC_TYPE.ALL_DOC && filterCount > 0) {
      setIsDisabledSaveSearchBtn(false);
    } else {
      setIsDisabledSaveSearchBtn(true);
    }
  }, [editDocsName, viewDocName, queryData, filterCount]);

  const [allDocumentsCount, setAllDocumentsCount] = useState<number>(0);
  const title = isNlpSearch ? nlpIndexId : index?.fieldTemplateId;
  const fieldTemplateIdRef = useRef(title);

  const type = useMemo(() => {
    if (globalSelectedScopeItem?.id || savedSearchScope) {
      return SEARCH_SCOPE_TYPE.SAVED_SEARCH;
    }
    return isNlpSearch ? SEARCH_SCOPE_TYPE.NLP : SEARCH_SCOPE_TYPE.ADHOC;
  }, [globalSelectedScopeItem?.id, savedSearchScope]);

  useEffect(() => {
    fieldTemplateIdRef.current = title;
  }, [title]);

  useEffect(() => {
    return () => {
      dispatch(setFilterList(defaultFilterModel));
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      abortControllers.forEach((controller) => {
        controller.abort();
      });
      abortControllers.clear();
    };
  }, []);

  useEffect(() => {
    if (isNlpSearch) {
      dispatch(
        setColumnsList({ isNlpSearch, columns: getDefaultDocListColumns() })
      );
    }
    if (getDefaultDocListColumns().length > 0) {
      fetchColumnsDetails();
    }
  }, []);

  useEffect(() => {
    if (updatedAllGridDocuments?.length > 0) {
      setRows(updatedAllGridDocuments);
      dispatch(setUpdateAllGridDocuments([]));
    }
  }, [updatedAllGridDocuments]);

  useEffect(() => {
    if (paginationDefaultLoad) {
      setStartIndex(1);
      setSelectionModel([]);
      setSelectedItem(null);
      dispatch(setPaginationDefaultLoad(false));
      if (viewDocType === LOAD_DOC_TYPE.ALL_DOC) {
        setDocSetIdForAllDocsList(undefined);
      }
    }
  }, [dispatch, paginationDefaultLoad, viewDocType]);

  const fetchColumnsDetails = async () => {
    try {
      const params = `clientId=${clientId}&projectId=${projectId}&top=10000`;
      const results = await getEcaFields(params);
      if (results?.data) {
        const allFields = (results?.data?.data || []).filter(
          (item: IFieldModal) =>
            item.fieldCategory == 'STANDARD_METADATA' ||
            item.fieldCategory == 'CUSTOM_METADATA' ||
            item.fieldCategory == 'TAG_FIELD'
        );
        const resultData = allFields.map((col: IFieldModal) => ({
          name: col.fieldName,
          label: col.fieldLabel,
          dataType: col.fieldESType?.toLowerCase(),
          UIDataType: col.fieldUIType?.toLowerCase(),
          fieldId: col.fieldId,
          isSortable: col.isSortable,
        }));
        setAllColumnsDetails(resultData);
      }
    } catch (error) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    }
  };

  const generateSummary = async (paramsRowId: string, documentId: string) => {
    try {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await GenerateSummary(params, documentId);

      if (result.status === 200) {
        documentSummarySseCall(
          result?.data?.data?.requestId,
          documentId,
          paramsRowId
        );
      } else {
        showToast(
          t('ecaDocList.errorGenerateSummary'),
          'error',
          undefined,
          'center'
        );
        handleSummaryRow(paramsRowId, false);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
      handleSummaryRow(paramsRowId, false);
    }
  };

  const documentSummarySseCall = (
    requestId: string,
    documentId: string,
    paramsRowId: string
  ) => {
    if (requestId && requestId != '') {
      const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.SEARCH_SERVICE}`;
      const eventSource = new EventSource(SSE_URL);
      eventSource.onmessage = (event) => {
        const responseData = JSON.parse(event.data);
        if (responseData.status.toLowerCase() === 'completed') {
          fetchGeneratedSummary(documentId, paramsRowId);
          eventSource.close();
        }
      };
      eventSource.onerror = (_error) => {
        showToast(
          t('ecaDocList.errorGenerateSummary'),
          'error',
          undefined,
          'center'
        );
        eventSource.close();
        handleSummaryRow(paramsRowId, false);
      };
    }
  };

  const fetchGeneratedSummary = async (
    documentId: string,
    paramsRowId: string,
    attempt = 1
  ): Promise<void> => {
    try {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await fetchSummary(params, documentId);
      const summary = result?.data?.data?.documentSummary;

      if (summary) {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === paramsRowId
              ? {
                ...row,
                nlp_summary_text: summary,
                loadingSummary: false,
              }
              : row
          )
        );
        handleRowExpansion(paramsRowId);
      } else if (attempt < MAX_ATTEMPTS) {
        await waitBeforeRetry(DELAY_MS);
        await fetchGeneratedSummary(documentId, paramsRowId, attempt + 1);
      } else {
        showToast(
          t('ecaDocList.errorGenerateSummary'),
          'error',
          undefined,
          'center'
        );
        handleSummaryRow(paramsRowId, false);
      }
    } catch (e) {
      if (attempt < MAX_ATTEMPTS) {
        await waitBeforeRetry(DELAY_MS);
        await fetchGeneratedSummary(documentId, paramsRowId, attempt + 1);
      } else {
        const err = e as AxiosError;
        showErrorToast(showToast, err, 'center');
        handleSummaryRow(paramsRowId, false);
      }
    }
  };

  const handleSummaryRow = (paramsRowId: string, loadingSummary: boolean) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === paramsRowId ? { ...row, loadingSummary: loadingSummary } : row
      )
    );
  };

  const updateGridDetails = async (data: SavedSearchDetails) => {
    const params = `clientId=${clientId}&projectId=${projectId}&top=10000`;
    try {
      setLoader(true);
      const url = `/field?${params}`;
      if (apiControllers?.has(url)) {
        apiControllers?.get(url)?.abort();
      }
      const controller = new AbortController();
      apiControllers?.set(url, controller);
      const results = await getEcaFields(params, controller.signal);
      apiControllers?.delete(url);
      if (results?.data) {
        const allFields = (results?.data?.data || []).filter(
          (item: IFieldModal) =>
            item.fieldCategory == 'STANDARD_METADATA' ||
            item.fieldCategory == 'CUSTOM_METADATA' ||
            item.fieldCategory == 'TAG_FIELD'
        );

        const resultData = allFields.map((col: IFieldModal) => ({
          name: col.fieldName,
          label: col.fieldLabel,
          dataType: col.fieldESType?.toLowerCase(),
          UIDataType: col.fieldUIType?.toLowerCase(),
          fieldId: col.fieldId,
          isSortable: col.isSortable,
        }));
        const availableColList = data.columnOrder
          .map((colName: string) =>
            resultData.find((col: ArrayItemType) => {
              return col.name == colName;
            })
          )
          .filter(Boolean);

        const availableSortList = data.sortOrder
          .map((order: { column: string; direction: string }) => {
            return {
              ...order,
              ...resultData.find((col: ArrayItemType) => {
                return col.name == order.column;
              }),
            };
          })
          .filter(Boolean);

        const availableFilterList = transformJSONToFilters(data.filterSet);
        filtersToApplyRef.current = availableFilterList;
        dispatch(setColumnsList({ isNlpSearch, columns: availableColList }));
        dispatch(
          setSortOrder({
            isNlpSearch,
            sortOrder: availableSortList,
          })
        );
      }
    } catch (e: any) {
      if (e.name === 'CanceledError') {
        return;
      }
      apiControllers?.delete(`/field?${params}`);
      const availableColList = data.columnOrder
        .map((colName: string) =>
          getDefaultDocListColumns().find((col: ArrayItemType) => {
            return col.name == colName;
          })
        )
        .filter(Boolean);

      const availableSortList = data.sortOrder
        .map((order: { column: string; direction: string }) => {
          return {
            ...order,
            ...getDefaultDocListColumns().find((col: ArrayItemType) => {
              return col.name == order.column;
            }),
          };
        })
        .filter(Boolean);

      const availableFilterList = transformJSONToFilters(data.filterSet);
      filtersToApplyRef.current = availableFilterList;
      dispatch(setColumnsList({ isNlpSearch, columns: availableColList }));
      dispatch(setSortOrder({ isNlpSearch, sortOrder: availableSortList }));
    } finally {
      setLoader(false);
    }
  };

  const transformJSONToFilters = (filterSet: {
    linkOperator: string;
    items: any[];
  }): GridFilterModel => {
    const filterModel: GridFilterModel = {
      items: [],
      logicOperator: GridLogicOperator.Or,
    };

    filterSet.items.forEach((filterItem, index) => {
      if (Array.isArray(filterItem.items)) {
        const groupedFilters = filterItem.items.map(
          (subItem: any, cIndex: number) => ({
            field: subItem.column,
            operator: subItem.operator,
            value: subItem.value,
            id: index + 1 + cIndex + 1,
          })
        );
        filterModel.items.push(...groupedFilters);
      } else {
        filterModel.items.push({
          field: filterItem.column,
          operator: filterItem.operator,
          value: filterItem.value,
          id: index + 1,
        });
      }
    });

    return filterModel;
  };

  useEffect(() => {
    if (isDocListContextStale) {
      return;
    }

    const updatedFilters = filterList?.items.map((item: GridFilterItem) => {
      return {
        field: item.field,
        operator: item.operator,
        value: item.value,
      };
    });
    handleApplyFilter({
      logicOperator: filterList.logicOperator,
      items: updatedFilters,
    });
  }, [filterList, isDocListContextStale]);

  const loadSaveFlag = useRef<boolean>(true);
  const columnsRef = useRef<string[]>([]);
  const prevOrderedFields = useRef<string[]>([]);
  const filtersToApplyRef = useRef<GridFilterModel | null>(null);

  useEffect(() => {
    if (
      JSON.stringify(columnsRef.current) !==
      JSON.stringify(prevOrderedFields.current)
    ) {
      prevOrderedFields.current = columnsRef.current;
    }
    if (columnsRef.current.length > 0) {
      if (!filtersToApplyRef.current) return;

      const allFieldsAvailable = filtersToApplyRef.current.items.every(
        (filter) => columnsRef.current.includes(filter.field)
      );

      if (allFieldsAvailable) {
        dispatch(setFilterList(filtersToApplyRef.current));
        filtersToApplyRef.current = null;
      }
    }
  }, [columnsRef.current]);

  useEffect(() => {
    if (
      viewDocName === LOAD_DOC_TYPE.SAVED_DOC &&
      globalSavedSearchDetails &&
      searchID
    ) {
      loadSaveFlag.current = true;
      updateGridDetails(globalSavedSearchDetails);
    }
  }, [viewDocName, searchID, globalSavedSearchDetails]);

  useEffect(() => {
    if (
      ((viewDocType === LOAD_DOC_TYPE.LOAD_SCOPE ||
        viewDocType === LOAD_DOC_TYPE.TAGS_SCOPE ||
        viewDocType === LOAD_DOC_TYPE.SAVE_SEARCHES_SCOPE) &&
        globalSelectedScopeItem?.id) ||
      (runSearchError && runSearchError?.message?.length > 0)
    ) {
      setRows([]);
      runFlag.current = false;
    }
  }, [viewDocType, globalSelectedScopeItem?.id, runSearchError?.message]);

  const mapResponse = (
    results: IDocListResponse,
    paginationStartIndex: number
  ) => {
    if (results?.data?.docList) {
      const startIndex = ((paginationStartIndex - 1) * itemsPerPage) + 1;
      const formatResponse = results.data.docList.map((item, index) => {
        const formattedItem: FormattedItem = {
          id: String(index + startIndex),
          documentid: item.docId,
        };
        item.metadata?.forEach((meta) => {
          formattedItem[meta.name] = meta.value == 'null' ? '' : meta.value;
        });
        return formattedItem;
      });
      setRows(formatResponse);
      setAllDocumentsCount(results.data.totalHits);
      handleGlobalSelectedItem?.(viewDocName);
      setExpandedRowIds([]);

      // scroll reset to top
      requestAnimationFrame(() => {
        mainScrollRef?.current?.scrollTo({
          top: 0,
          behavior: 'auto',
        });
      });
      loadSaveFlag.current = false;
      if (selectedItem === SelectedItems.allInDocument) {
        const allRowIds = formatResponse.map((row) => row.id);
        setSelectionModel(allRowIds);
      }
    } else {
      setAllDocumentsCount(0);
      setRows([]);
    }
    setTimeout(() => {
      setLoader(false);
      dispatch(setSearchLoader(false));
      runFlag.current = true;
    }, 100);
  };

  const getDocListResults = async (
    filters: IFilterSet,
    pageColumns: any,
    pageSortOrder: any,
    reqParams: string,
    paginationStartIndex: number,
    docSetIdForNLP?: string
  ) => {
    setLoader(true);

    const reqBody: IDocListReqBody = {
      metaDataFields: pageColumns
        .map((col: any) => col.name)
        .concat('nlp_summary_text'),
      sortOrder: getSortOrder(),
      filterSet: filters,
    };
    const getDocSetId = isNlpSearch ? docSetIdForNLP : docSetId;
    const numberDocSetId = Number.parseInt(getDocSetId ?? '');
    if (!Number.isNaN(numberDocSetId)) {
      reqBody.docSetId = numberDocSetId;
    }

    try {
      if (searchID) {
        const url = `/searches/${searchID}/results` + reqParams;
        if (apiControllers?.has(url)) {
          apiControllers?.get(url)?.abort();
        }
        const controller = new AbortController();
        apiControllers?.set(url, controller);
        const results: IDocListResponse = await getSearchResults(
          reqParams,
          searchID,
          reqBody,
          controller.signal
        );
        if (isNlpSearch && isEmpty(docSetIdForNLPIndex)) {
          setDocSetIdForNLPIndex(results.data.docSetId);
        }
        mapResponse(results, paginationStartIndex);
        apiControllers?.delete(url);
      }
    } catch (error: any) {
      setRows([]);
      setLoader(false);
      dispatch(setSearchLoader(false));
      if (error.name === 'CanceledError') {
        return;
      }
      const errorMessages =
        (error?.response?.data?.meta?.message
          ? error.response.data.meta.message + '. '
          : '') +
        (error?.response?.data?.validationErrors
          ?.map((err: any) => err.message)
          .join(' ') || '');
      dispatch(setSearchResultError(errorMessages));
      apiControllers?.delete(`/searches/${searchID}/results` + reqParams);
      queryData.queryBuilderMode &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.ERROR,
          error: {
            code: error.code,
            type: GA_ERROR_TYPES.VALIDATION_ERROR,
            text: errorMessages,
          },
        });
    }
  };

  const getAllDocumentResults = async (
    filters: IFilterSet,
    pageColumns: any,
    pageSortOrder: any,
    reqParams: string,
    paginationStartIndex: number,
    currentFieldTemplateId: string,
    docSetIdForAllDocs?: any
  ) => {
    setLoader(true);

    const reqBody: IDocListReqBody = {
      metaDataFields: pageColumns
        .map((col: any) => col.name)
        .concat('nlp_summary_text'),
      sortOrder: pageSortOrder.map((order: any) => ({
        column: order.column,
        direction: order.direction,
      })),
      filterSet: filters,
    };
    const numberDocSetId = Number.parseInt(docSetIdForAllDocs ?? '');
    if (!Number.isNaN(numberDocSetId)) {
      reqBody.docSetId = numberDocSetId;
    }

    try {
      if (currentFieldTemplateId) {
        const url = `/indexes/${currentFieldTemplateId}/alldoclist` + reqParams;
        if (apiControllers?.has(url)) {
          apiControllers?.get(url)?.abort();
        }
        const controller = new AbortController();
        apiControllers?.set(url, controller);

        const results: IDocListResponse = await getAllDocResults(
          reqParams,
          currentFieldTemplateId,
          reqBody,
          controller.signal
        );
        setDocSetIdForAllDocsList(results.data.docSetId);
        setRunSearchError &&
          setRunSearchError({
            message: '',
            runSearchDisabled: false,
          });
        mapResponse(results, paginationStartIndex);
        apiControllers?.delete(url);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      setRows([]);
      setLoader(false);
      dispatch(setSearchLoader(false));
      if (err.name === 'CanceledError') {
        return;
      }
      showToast(err.message, 'error', undefined, 'center');
      apiControllers?.delete(
        `/indexes/${currentFieldTemplateId}/alldoclist` + reqParams
      );
    }
  };

  const debouncedGetResults = useCallback(
    debounce(
      (filterSet, pageColumns, pageSortOrder, itemsPerPage, startIndex) => {
        const rowNumber = ((startIndex - 1) * itemsPerPage) + 1
        const updatedParams = `?clientId=${clientId}&projectId=${projectId}&pageSize=${itemsPerPage}&rowNumber=${rowNumber}&type=${type}`;
        const currentFieldTemplateId = fieldTemplateIdRef.current;
        if (
          viewDocName === LOAD_DOC_TYPE.ALL_DOC &&
          !isDocListContextStale
        ) {
          getAllDocumentResults(
            filterSet,
            pageColumns,
            pageSortOrder,
            updatedParams,
            startIndex as number,
            currentFieldTemplateId as string,
            docSetIdForAllDocsList
          );
        } else if (editDocsName === LOAD_DOC_TYPE.SAVED_SEARCHES_EDIT) {
          setRows([]);
          setFilterCount(0);
          runFlag.current = false;
        } else if (
          (searchID &&
            viewDocName !== LOAD_DOC_TYPE.SAVED_DOC &&
            viewDocName !== LOAD_DOC_TYPE.ALL_DOC) ||
          (searchID &&
            viewDocName == LOAD_DOC_TYPE.SAVED_DOC &&
            !loadSaveFlag.current)
        ) {
          getDocListResults(
            filterSet,
            pageColumns,
            pageSortOrder,
            updatedParams,
            startIndex as number,
            docSetIdForNLPIndex
          );
        }
      },
      500
    ), // Adjust the delay time (in ms)
    [
      viewDocName,
      editDocsName,
      searchID,
      clientId,
      projectId,
      type,
      pageSortOrder,
      selectedItem,
      docSetIdForAllDocsList,
      docSetIdForNLPIndex,
      isDocListContextStale,
    ]
  );

  useEffect(() => {
    if (isDocListContextStale) {
      return;
    }

    debouncedGetResults(
      filterSet,
      pageColumns,
      pageSortOrder,
      itemsPerPage,
      startIndex
    );
    return () => debouncedGetResults.cancel();
  }, [
    filterSet,
    viewDocName,
    searchID,
    pageColumns,
    pageSortOrder,
    itemsPerPage,
    startIndex,
    isDocListContextStale,
  ]);

  const handleSummaryGen = useCallback(
    (
      params: GridRenderCellParams<docListResponseType, GridTreeNodeWithRender>
    ) => {
      const { id: paramsRowId, documentid } = params.row;
      handleSummaryRow(paramsRowId, true);
      generateSummary(paramsRowId, documentid);
    },
    []
  );
  handleSummaryGenRef.current = handleSummaryGen;

  useEffect(() => {
    dispatch(setEcaDocZoom(100));
  }, [!ecaDocumentViewer]);

  // Toggle row expansion
  const handleRowExpansion = useCallback((id: string) => {
    setExpandedRowIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);
  handleRowExpansionRef.current = handleRowExpansion;

  const handleDropdownSelect = useCallback((key: SelectedItemsTypes | null) => {
    if (key) {
      const allRowIds = apiRef.current.getAllRowIds();
      setSelectionModel(allRowIds as string[]);
    } else {
      setSelectionModel([]);
    }
    setSelectedItem(key);
  }, []);

  handleDropdownSelectRef.current = handleDropdownSelect;

  const getTypeFromData = (type: string) => {
    switch (type.toLowerCase()) {
      case 'keyword':
      case 'text':
        return 'string';
      case 'integer':
      case 'float':
      case 'long':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'string';
    }
  };

  const getColumnFilterOperators = useCallback(
    (column: any) => {
      if (
        column.dataType.toLowerCase() == 'keyword' ||
        column.UIDataType === 'tag'
      ) {
        return getGridStringOperators()
          .filter((operator) => {
            return (
              operator.value == 'contains' ||
              operator.value == 'doesNotContain' ||
              operator.value == 'equals' ||
              operator.value == 'doesNotEqual'
            );
          })
          .map((operator) => ({
            ...operator,
            InputComponent: (params: GridFilterInputValueProps) => (
              <CustomFilterInput
                params={params}
                type={'text'}
                loader={loader}
              />
            ),
          }));
      } else if (getTypeFromData(column.dataType) == 'number') {
        return getGridNumericOperators()
          .map((operator) => {
            switch (operator.value) {
              case '=':
                return { ...operator, label: 'Is' };
              case '!=':
                return { ...operator, label: 'Is not' };
              case '>=':
                return {
                  ...operator,
                  label: 'Is greater than or equal to',
                };
              case '<=':
                return { ...operator, label: 'Is less than or equal to' };
              default:
                return operator;
            }
          })
          .filter((operator) => {
            return (
              operator.value != 'isEmpty' &&
              operator.value != 'isNotEmpty' &&
              operator.value != 'isAnyOf' &&
              operator.value != '>' &&
              operator.value != '<'
            );
          })
          .map((operator) => ({
            ...operator,
            InputComponent: (params: GridFilterInputValueProps) => (
              <CustomFilterInput
                params={params}
                type={'number'}
                loader={loader}
              />
            ),
          }));
      } else if (getTypeFromData(column.dataType) == 'date') {
        return getGridDateOperators()
          .filter((operator) => {
            return (
              operator.value != 'isEmpty' &&
              operator.value != 'isNotEmpty' &&
              operator.value != 'isAnyOf'
            );
          })
          .concat({
            label: 'Between',
            value: 'between',
            headerLabel: 'Between',
            getApplyFilterFn: (filterItem) => {
              return filterItem.value;
            },
          });
      } else {
        return getGridStringOperators()
          .filter((operator) => {
            return (
              operator.value == 'contains' ||
              operator.value == 'doesNotContain' ||
              operator.value == 'startsWith' ||
              operator.value == 'endsWith'
            );
          })
          .map((operator) => ({
            ...operator,
            InputComponent: (params: GridFilterInputValueProps) => (
              <CustomFilterInput
                params={params}
                type={'text'}
                loader={loader}
              />
            ),
          }));
      }
    },
    [loader]
  );

  const dynamicColumnsList: GridColDef[] = useMemo(() => {
    const updatedColumns: GridColDef[] = pageColumns.map((column) => {
      const columnData = {
        ...column,
        field: column.name,
        headerName: column.label,
        type: getTypeFromData(column.dataType),
        flex: 1,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        filterOperators: getColumnFilterOperators(column),
        renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
          if (column.name == 'confidence_score') {
            return null;
          }
          return (
            <HeaderColumnFilter
              column={column}
              params={params}
              apiRef={apiRef}
              currentCol={currentCol}
            />
          );
        },
      };

      if (column.name == 'file_type') {
        return {
          ...columnData,
          renderCell: (params) => {
            const getFileTypeIcon = (fileType: string) => {
              const Icon = fileTypeToIconMap[fileType as keyof FileTypeToIconMap];
              if (Icon) {
                return (
                  <AurIcon
                    icon={Icon}
                    color={IconColors.Custom}
                    customClassName={'file-type-icon'}
                  />
                );
              }
              return (
                <AurIcon
                  icon={IconNames.OtherDocumentIcon}
                  color={IconColors.Custom}
                  customClassName={'file-type-icon'}
                />
              );
            };
            return (
              <Box className="doc-type-header" tabIndex={0}>
                {params.row.file_type &&
                  getFileTypeIcon(params.row.file_type.toLowerCase())}
                {params.row.file_type}
              </Box>
            );
          },
          headerName: column.label,
          filterable: true,
          resizable: true,
          display: 'flex',
          align: 'left',
        };
      }
      if (column.name == 'documentid') {
        return {
          ...columnData,
          renderCell: (params) => {
            const handleClick = () => {
              trackGAEventRef.current({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.ecaDocList.viewTooltip'),
                  type: GA_CLICK_TYPES.LIST_ITEM,
                  region: GA_REGIONS.BODY,
                  action: t('gaTracking.ecaDocList.viewDocumentAction'),
                },
              });
              dispatch(setStrSearchFlag(false));
              dispatch(setEcaDocumentViewerModal(true));
              trackGAEventRef.current({
                eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                subPage: {
                  subpage_name: t('gaTracking.ecaDocList.documentPreview'),
                  subpage_type: GA_SUBPAGE_TYPE.MODAL,
                },
              });
              setSelectedDocLink(params.row.documentid);
              const allDocIds = rowsRef.current.map((row) => row.documentid);
              dispatch(setAllGridDocuments(rowsRef.current));
              dispatch(
                setCreateSummaryAllowed(hasDocumentSummaryGenerationPerm ?? false)
              );
              setSelectionDocuments(allDocIds);
              setScrollFetching(true);
            };
            return (
              <span
                style={{ cursor: hasDocumentPreviewPerm ? 'pointer' : '' }}
                className={hasDocumentPreviewPerm ? `doc-id-header` : ''}
                onClick={() => {
                  hasDocumentPreviewPerm && handleClick();
                }}
                tabIndex={0}
              >
                {params.row.documentid}
              </span>
            );
          },
        };
      }
      if (column.name == 'confidence_score') {
        return {
          ...columnData,

          renderCell: (params) => {
            return Number(params.row.confidence_score) ? (
              <Box className="confidence-score">
                <ConfidenceScore score={params.row.confidence_score} />
                <Typography className="typo-text body3_size" tabIndex={0}>
                  {Number(params.row.confidence_score) === 1
                    ? '1'
                    : `.${Number(params.row.confidence_score)?.toFixed(2).substring(2)}`}
                </Typography>
              </Box>
            ) : null;
          },
          width: 117,
          minWidth: 117,
          filterable: false,
        };
      }
      if (column.name == 'family_relationship') {
        return {
          ...columnData,
          renderCell: (params) => {
            const statusText = params.row.family_relationship
              ? params.row.family_relationship
              : '';
            const text = statusText
              .split('/')
              .map((item: string) => item.slice(0, 1))
              .join('')
              .toUpperCase();
            function getAvatarColor(text: string) {
              switch (text) {
                case FAMILY_STATUS.P:
                  return t('ecaDocList.avatarClasses.parent');
                case FAMILY_STATUS.C:
                  return t('ecaDocList.avatarClasses.child');
                case FAMILY_STATUS.S:
                  return t('ecaDocList.avatarClasses.standAlone');
                case FAMILY_STATUS.PC:
                  return t('ecaDocList.avatarClasses.parentChild');
              }
            }

            return (
              <div className="avatar-container">
                {params.row.family_relationship && (
                  <Avatar
                    className={`avatar-box ${getAvatarColor(text)}`}
                    variant="square"
                  >
                    <Typography
                      className={`avatar-text ${getAvatarColor(text)} `}
                      tabIndex={0}
                    >
                      {text === FAMILY_STATUS.PC ? (
                        <>
                          <span className="first-letter">{text[0]}</span>
                          <span className="last-letter">{text[1]}</span>
                        </>
                      ) : (
                        text
                      )}
                    </Typography>
                  </Avatar>
                )}
                {params.row.family_relationship}
              </div>
            );
          },
        };
      }
      if (getTypeFromData(column.dataType) == 'date') {
        return {
          ...columnData,
          renderCell: (params) => {
            return (
              <AurLightTooltip title={t('common.yearMonthDate')}>
                <span>{params.formattedValue}</span>
              </AurLightTooltip>
            );
          },
          valueGetter: (value) => {
            if (!value || value === 'null') {
              return '';
            }
            return value;
          },
          valueFormatter: (value) => {
            if (!value) {
              return '';
            }
            return value;
          },
        };
      }

      if (column.UIDataType === 'tag') {
        return {
          ...columnData,
          renderCell: (params: any) => {
            const value = params.row[column.name];

            if (!value) return null;

            return value.length > 25 ? (
              <LightTooltip title={value}>
                <div className="data-set-name" tabIndex={0}>
                  {value}
                </div>
              </LightTooltip>
            ) : (
              <div className="data-set-name" tabIndex={0}>
                {value}
              </div>
            );
          },
        };
      }
      return {
        ...columnData,
      };
    })
    return updatedColumns;
  }, [pageColumns, getColumnFilterOperators]);

  const getIdColumnWidth = (rows: docListResponseType[]) => {
    const maxIdLength = Math.max(
      ...rows.map((row: docListResponseType) => row.id.toString().length)
    );
    const baseWidth = 40;
    const extraWidth = maxIdLength > 2 ? (maxIdLength - 2) * 8 : 0; // 8px per extra character
    return baseWidth + extraWidth;
  };
  const idColumnWidth = getIdColumnWidth(rows);
  const hasFileTypeColumn = pageColumns.some((col) => col.name == 'file_type');

  const onCheckboxMenuSelectedChange = useCallback((key: SelectedItemsTypes | null) => {
    if (key) {
      handleDropdownSelectRef.current?.(key);
    } else {
      handleDropdownSelectRef.current?.(null);
    }
  }, []);

  const fixedColumnsList: GridColDef<(typeof rows)[number]>[] = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: 'id',
        renderHeader: () => <span className="sr-only">id</span>,
        renderHeaderFilter: () => <span className="sr-only">id</span>,
        sortable: false,
        filterable: false,
        disableReorder: true,
        display: 'flex',
        headerClassName: 'no-column-separator',
        align: 'center',
        minWidth: 40,
        width: idColumnWidth,
        resizable: true,
        renderCell: (params) => <Box className="id-cell-box">{params.value}</Box>,
      },
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        sortable: false,
        filterable: false,
        disableReorder: true,
        minWidth: 48,
        width: 48,
        display: 'flex',
        resizable: false,
        headerClassName: 'eca-checkbox-header no-column-separator',
        cellClassName: 'eca-checkbox-cell',
        renderHeaderFilter: () => <span className="sr-only">checkbox</span>,
        renderHeader: () => (
          <Box className="eca-aur-button" component={'div'} aria-live="polite">
            <SelectAllHeaderCheckbox />
            <CheckboxHeaderMenu
              ref={checkboxHeaderMenuRef}
              onSelectedChange={onCheckboxMenuSelectedChange}
            />
          </Box>
        ),
        renderCell: (params) => {
          const api = params.api;
          const isSelected = api.isRowSelected(params.id);
          const isSelectable = api.isRowSelectable(params.id);
          return (
            <AurCheckbox
              className="eca-row-checkbox"
              checked={isSelected}
              disabled={!isSelectable}
              onChange={() => api.selectRow(params.id, !isSelected)}
              data-testid={`eca-checkbox-${params.id}`}
              inputProps={{ 'aria-label': `${t('aurCheckbox.checkedCheckbox')} ${params.id}` }}
            />
          );
        },
      },
      {
        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        headerName: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        renderHeader: () => (
          <span className="sr-only">{GRID_DETAIL_PANEL_TOGGLE_FIELD}</span>
        ),
        renderHeaderFilter: () => (
          <span className="sr-only">{GRID_DETAIL_PANEL_TOGGLE_FIELD}</span>
        ),
        sortable: false,
        filterable: false,
        disableReorder: true,
        display: 'flex',
        headerClassName: `${hasFileTypeColumn ? 'no-column-separator' : ''}`,
        resizable: false,
        align: 'center',
        width: 32,
        minWidth: 32,
        renderCell: (params) => {
          if (params.row.loadingSummary) {
            return (
              <Box
                className="summary-pregress-box"
                data-testid={`summary-progress-${params.id}`}
              >
                <AurLoadingIndicator show={true} size={24} />
              </Box>
            );
          }

          const getSummaryIcon = (data: docListResponseType) => {
            const { nlp_summary_text, id } = data;
            if (nlp_summary_text) {
              return (
                <LightTooltip title={t('ecaDocList.toolTip.summary.view')}>
                  <IconButton
                    onClick={() => {
                      handleRowExpansionRef.current?.(id);
                      trackGAEventRef.current({
                        eventName: GA_EVENT_NAMES.CLICK,
                        click: {
                          name: t('ecaDocList.toolTip.summary.view'),
                          region: GA_REGIONS.BODY,
                          type: GA_CLICK_TYPES.ICON,
                          action: t('gaTracking.ecaDocList.viewDocumentSummary'),
                        },
                      });
                    }}
                    data-testid={`summary-expand-${id}`}
                  >
                    <img src={ShowSummarySVG} alt="show summary" />
                  </IconButton>
                </LightTooltip>
              );
            }

            if (hasDocumentSummaryGenerationPerm) {
              if (nlpSearchEnabled) {
                return (
                  <LightTooltip title={t('ecaDocList.toolTip.summary.generate')}>
                    <IconButton
                      onClick={() => {
                        handleSummaryGenRef.current?.(params);
                        trackGAEventRef.current({
                          eventName: GA_EVENT_NAMES.CLICK,
                          click: {
                            name: t('ecaDocList.toolTip.summary.generate'),
                            region: GA_REGIONS.BODY,
                            type: GA_CLICK_TYPES.ICON,
                            action: t(
                              'gaTracking.ecaDocList.generatedDocumentSummary'
                            ),
                          },
                        });
                      }}
                      data-testid={`summary-generate-${id}`}
                    >
                      <img src={GenerateSummarySVG} alt="generate summary" />
                    </IconButton>
                  </LightTooltip>
                );
              } else {
                return (
                  <LightTooltip title={t('ecaDocList.toolTip.summary.lock')}>
                    <IconButton
                      onClick={() => {
                        setRows((prevRows) =>
                          prevRows.map((row) =>
                            row.id === id
                              ? {
                                ...row,
                                nlpNotEnabled: true,
                              }
                              : row
                          )
                        );
                        handleRowExpansionRef.current?.(id);
                      }}
                      data-testid={`summary-lock-expand-${id}`}
                    >
                      <img src={NlpSummarySVG} alt="lock summary" />
                    </IconButton>
                  </LightTooltip>
                );
              }
            }
          };
          return (
            <Box>
              {params.row.file_type &&
                SUMMARY_FILE_TYPE.includes(params.row.file_type.toLowerCase()) ? (
                <>{getSummaryIcon(params.row)}</>
              ) : (
                ''
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    idColumnWidth,
    hasFileTypeColumn,
    onCheckboxMenuSelectedChange,
    hasDocumentSummaryGenerationPerm,
    nlpSearchEnabled,
  ]);

  const handleSelectAllChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (
        event.target.checked &&
        !(selectionModel.length > 0 && selectionModel.length < rows.length)
      ) {
        const allRowIds = apiRef.current.getAllRowIds();
        setSelectionModel(allRowIds as string[]);
        setSelectedItem(SelectedItems.allInDocument);
        checkboxHeaderMenuRef.current?.setSelectedOption(SelectedItems.allInDocument);

        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.ecaDocList.checkAll'),
            region: GA_REGIONS.BODY,
            type: GA_CLICK_TYPES.CHECKBOX,
            action: t('gaTracking.ecaDocList.selectedAllDocuments'),
          },
        });
      } else {
        setSelectionModel([]);
        setSelectedItem(null);
        checkboxHeaderMenuRef.current?.setSelectedOption(null);
      }
    },
    [selectionModel.length, rows.length, trackGAEvent]
  );

  const saveSearchBtnClick = () => {
    onSaveSearchClick?.(builder, index.fieldTemplateId);

    //track event on search button click on bottom
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.queryBuilder.savedSearchName', {
          buttonPlace: 'bottom',
        }),
        region: GA_REGIONS.BODY,
        type: GA_CLICK_TYPES.BUTTON,
        action: t('gaTracking.queryBuilder.openedSearch'),
        details: t('gaTracking.queryBuilder.numberOfComponents', {
          length: countRules(builder),
        }),
      },
    });
  };

  const setShowBulkTaggingModal = (visible: boolean) => {
    setShowBulkTaggingModalVisible(visible);
    if (visible)
      setTimeout(() => {
        bulkTaggingRef.current?.focus();
      }, 100);
  };

  const handleTagClick: MouseEventHandler<HTMLButtonElement> = (_e) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('common.pagination.tagTooltip'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.ecaDocList.gaStep1'),
      },
    });
    if (isNlpSearch) {
      // // Not to show family member modal when bulk tagging for NLP search
      setShowBulkTaggingModal(true);
    } else {
      // // show family member modal for Index search, when family (group identifier) from relational group not selected OR default document View where searchID is not present
      !searchID || hasSelectedFamilyGroupIdentifier
        ? setShowBulkTaggingModal(true)
        : setShowFamilyMembersWarningModal(true);
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.bulkTaggingtitle'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };

  const selectedDocumentIds = useMemo(() => {
    return rows
      .filter((document) => selectionModel.includes(document.id))
      .map((document) => document.documentid);
  }, [selectionModel, rows]);

  const handleViewClick = () => {

    if (selectedItemRef.current === SelectedItems.allInDocument) {
      setScrollFetching(true);
    } else {
      setScrollFetching(false);
    }

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.viewTooltip'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.ecaDocList.viewDocumentAction'),
      },
    });
    dispatch(setEcaDocumentViewerModal(true));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.documentPreview'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });

    setSelectionDocuments(selectedDocumentIds);
    setSelectedDocLink('');
  };

  const handleDownloadClick = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
      file_extension: GA_FILE_EXTENTIONS.CSV,
      click: {
        name: t('gaTracking.global.download'),
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.ecaDocList.downloadDocumentSubpage'),
      },
    });
    setShowSearchLoader(true);
    exportDocList();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.weAreExportingYourFiles'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const getSortOrder = () => {
    let filteredSortOrder: ISortOrder[] = [];
    if (
      isNlpSearch &&
      pageSortOrder.some((item: any) => item.column === 'confidence_score')
    ) {
      if (pageSortOrder.length > 1) {
        filteredSortOrder = pageSortOrder.filter(
          (item: any) => item.column !== 'confidence_score'
        );
      } else {
        filteredSortOrder = [];
      }
    } else {
      filteredSortOrder = pageSortOrder;
    }

    return filteredSortOrder.map((order: any) => ({
      column: order.column,
      direction: order.direction,
    }));
  };

  const exportDocList = async () => {
    const matchingFieldIds: number[] = pageColumns
      .map(
        (col) =>
          allColumnsDetails?.find((item) => item.name === col.name)?.fieldId
      )
      .filter((id): id is number => id !== undefined);
    let reqBody: IDocListExportReqBody = {
      filterSet: filterSet,
      fieldIds: matchingFieldIds,
      sortOrder: getSortOrder(),
    };
    if (
      selectedDocumentIds.length > 0 &&
      selectedItem !== SelectedItems.allInDocument
    ) {
      reqBody = {
        ...reqBody,
        docIdList: selectedDocumentIds,
      };
    }
    let paramsClientProject = `?clientId=${clientId}&projectId=${projectId}`;
    const searchId =
      viewDocName === LOAD_DOC_TYPE.SAVED_DOC && globalSavedSearchDetails
        ? globalSavedSearchDetails?.ecaSearchId
        : searchID;
    if (searchId) {
      paramsClientProject = paramsClientProject + `&searchId=${searchId}`;
    }

    const url = `/searches/results/export` + paramsClientProject;
    if (abortControllers.has(url)) {
      abortControllers.get(url)?.abort();
    }
    const controller = new AbortController();
    abortControllers.set(url, controller);

    try {
      const results = await exportDocumentList(
        paramsClientProject,
        searchId ?? '',
        reqBody,
        controller.signal
      );

      getSseDocumentExportUrl(results?.data?.data?.requestId);
      abortControllers.delete(url);
    } catch (error: unknown) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
      setShowSearchLoader(false);
      abortControllers.delete(url);
    }
  };

  const downloadSequentially = async (
    fileNames: string[],
    bucketKey: string,
    delayMs = 300
  ) => {
    const failedFiles: string[] = [];
    const successfulFiles: string[] = [];
    for (const fileName of fileNames) {
      try {
        await downloadExportDoc(fileName, bucketKey);
        successfulFiles.push(fileName);
      } catch {
        failedFiles.push(fileName);
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    setShowSearchLoader(false);

    if (successfulFiles.length > 0) {
      showToast(
        `${successfulFiles.length}${t('ecaDocList.successMsg')}`,
        'success',
        undefined,
        'center'
      );
    }

    if (failedFiles.length > 0) {
      showToast(
        `${failedFiles.length}${t('ecaDocList.failedMsg')}`,
        'error',
        undefined,
        'center'
      );
    }
  };

  const getSseDocumentExportUrl = (requestId: string) => {
    if (requestId && requestId != '') {
      const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.EXPORT_SERVICE}`;
      eventSourceRef.current = new EventSource(SSE_URL);
      let downloadStarted = false;
      eventSourceRef.current.onmessage = async (event) => {
        const responseData = JSON.parse(event.data);
        const status = responseData.status?.toLowerCase();

        if (status === 'failed') {          
          showToast(
            responseData.data?.Message,
            'error',
            undefined,
            'center'
          );
          eventSourceRef.current?.close();
          setShowSearchLoader(false);
          return;
        }

        if (status === 'completed') {
          downloadStarted = true;
          const fileNames = String(responseData.data.FileName || '')
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);

          await downloadSequentially(fileNames, responseData.data.BucketKey);
          eventSourceRef.current?.close();
        }
      };

      eventSourceRef.current.onerror = () => {
        if (!downloadStarted) {
          downloadStarted = true;
          downloadSequentially([requestId], 'doclistexport');
        }
        eventSourceRef.current?.close();
      };
    }
  };

  const downloadExportDoc = async (fileName: string, bucketKey: string) => {
    const params = `?clientId=${clientId}&projectId=${projectId}&fileName=${fileName}&bucketKey=${bucketKey}`;

    const url = `/document/download` + params;
    if (abortControllers.has(url)) {
      abortControllers.get(url)?.abort();
    }
    const controller = new AbortController();
    abortControllers.set(url, controller);

    try {
      const results = await downloadExportDocument(params, controller.signal);
      downloadResponseAsCSVInBlob(results?.data, fileName);
      abortControllers.delete(url);
    } finally {
      abortControllers.delete(url);
    }
  };

  const handleLoaderClose = (value: string | undefined) => {
    if (value == 'cancel') {
      //close the modal, cancel the api call, dont update the table
      setShowSearchLoader(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      abortControllers.forEach((controller) => {
        controller.abort();
      });
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.ecaDocList.cancelExportBtn'),
          type: GA_CLICK_TYPES.LINK,
          region: GA_REGIONS.MODAL,
          action: t('gaTracking.ecaDocList.cancelDownload'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaDocList.weAreExportingYourFiles'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  const apiRef = useGridApiRef();
  const mainScrollRef = useRef<HTMLDivElement | null>(null);

  const handleApplyFilter = useCallback(
    (filters: GridFilterModel) => {
      let items: GridFilterItem[] = [];
      if (filters?.items.length) {
        items = filters.items;
      }
      const checkUndefined = (items: GridFilterItem[]) => {
        return !items.some((item) => {
          return item.value == null || item.value == '';
        });
      };
      if (items.length == 0) {
        setFilterCount(0);
        prevFiltersRef.current = []; // Reset on clear
      }

      if (items && checkUndefined(items)) {
        // --- TRACKING LOGIC ---
        // ONLY track if the source is 'HEADER' (or null/initial)
        // If the source is 'ADVANCE', we skip tracking here because the Panel handles its own tracking
        setTimeout(() => {
          if (filterSourceRef.current !== 'ADVANCE') {
            const newlyTouchedItem = items.find((item) => {
              const prevItem = prevFiltersRef.current.find(
                (p) => p.field === item.field
              );
              return (
                (!prevItem || prevItem.value !== item.value) &&
                item.value !== '' &&
                item.value != null
              );
            });

            if (newlyTouchedItem) {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.FILTER,
                click: {
                  name: newlyTouchedItem.field,
                  region: GA_REGIONS.BODY,
                  type: GA_CLICK_TYPES.INPUT, // This is the 'normal' filter type
                  action: t('gaTracking.ecaDocList.filterInput'),
                },
              });
            }
          }
          filterSourceRef.current = null;
          prevFiltersRef.current = items;
        }, 800);

        // --- END TRACKING ---
        const filterSetData = transformFiltersToJSON(filters);
        setFilterSet(filterSetData?.filterSet);
        setStartIndex(1);
        if (
          viewDocName === LOAD_DOC_TYPE.SAVED_DOC &&
          globalSavedSearchDetails &&
          searchID &&
          loadSaveFlag.current
        ) {
          const updatedParams = `?clientId=${clientId}&projectId=${projectId}&pageSize=${itemsPerPage}&rowNumber=${startIndex}&type=${type}`;
          getDocListResults(
            filterSetData?.filterSet,
            pageColumns,
            pageSortOrder,
            updatedParams,
            startIndex,
            docSetIdForNLPIndex
          );
        }
      }
    },
    [filterList?.items, docSetIdForNLPIndex]
  );

  const transformFiltersToJSON = (filterModel: GridFilterModel) => {
    // Step 1: Group filters by column name
    const groupedFilters: Record<string, GridFilterItem[]> = {};
    filterModel.items.forEach((item) => {
      if (!groupedFilters[item.field]) {
        groupedFilters[item.field] = [];
      }
      groupedFilters[item.field].push(item);
    });
    // Step 2: Convert grouped filters to JSON
    const filterItems = Object.keys(groupedFilters).map((column) => {
      const columnFilters = groupedFilters[column].map((item) => ({
        column: item.field,
        operator: item.operator,
        value: item.value,
        fieldType:
          pageColumns.find((cols) => cols.name == item.field).dataType ?? '',
      }));
      return columnFilters.length > 1
        ? { linkOperator: 'OR', items: columnFilters } // OR for same column
        : columnFilters[0]; // Single filter case
    });
    setFilterCount(filterItems.length);
    return {
      filterSet: {
        linkOperator: 'AND', // AND between different columns
        items: filterItems,
      },
    };
  };

  const handleClearFilters = () => {
    dispatch(setFilterList(defaultFilterModel));
    setClearFilterModal(false);
  };

  const confirmationModalCloseHandler = () => {
    setShowFamilyMembersWarningModal(false);
  };

  const confirmationModalOkHandler = () => {
    setShowFamilyMembersWarningModal(false);
    setShowBulkTaggingModal(true);
  };

  const CustomNoRowsOverlay = () => {
    return (
      <div
        className={`${globalSelectedScopeItem?.id ||
          (rows.length === 0 && filterCount === 0 && !runFlag.current)
          ? 'initial-no-data-container'
          : 'no-rows-overlay-container'
          }`}
        data-testid="no-rows-eca"
      >
        <img
          src="assests/images/eca-no-results.svg"
          alt="ECA- No results"
          aria-hidden="true"
        />
        <Typography className="typo-text headline2_size type-head">
          {globalSelectedScopeItem?.id ||
            (rows.length === 0 && filterCount === 0 && !runFlag.current)
            ? t('ecaDocList.scopeNoResults')
            : t('ecaDocList.noResultsText')}
        </Typography>
      </div>
    );
  };

  const renderCellWithTabIndex = useCallback((params: any) => {
    const { value } = params;
    const tabIndex = params.tabIndex < 0 ? 0 : params;

    const cellContent = <span tabIndex={tabIndex}>{value}</span>;

    return cellContent;
  }, []);

  const withTabIndex = useCallback(
    (renderCell: (params: GridRenderCellParams) => React.ReactNode) => {
      return (params: GridRenderCellParams) => {
        const cellContent = renderCell(params); // Get the content of the cell
        let tabIndex = -1;
        if (isValidElement(cellContent) && cellContent.props.tabIndex > 0) {
          tabIndex = cellContent?.props?.tabIndex ?? -1;
        }

        return (
          <span tabIndex={tabIndex < 0 && !_.isEmpty(cellContent) ? 0 : tabIndex}>
            {cellContent}
          </span>
        );
      };
    },
    []
  );

  const updatedColumns: GridColDef[] = useMemo(
    () =>
      [...fixedColumnsList, ...dynamicColumnsList].map((col) => ({
        ...col,
        renderCell: col.renderCell
          ? withTabIndex(col.renderCell)
          : renderCellWithTabIndex,
      })),
    [fixedColumnsList, dynamicColumnsList]
  );

  const gridSlots = useMemo(() => ({
    noRowsOverlay: CustomNoRowsOverlay,
    noResultsOverlay: CustomNoRowsOverlay,
    filterPanelRemoveAllIcon: DeleteOutline,
    filterPanel: (params: GridFilterPanelProps) => (
      <CustomGridFilterPanel
        {...params}
        currentColumn={currentCol.current}
        loader={loader}
        onSetFilterSource={() => {
          filterSourceRef.current = 'ADVANCE';
        }}
      />
    ),
  }), [])

  const gridSlotProps = useMemo(() => ({
    baseSelect: {
      className: 'custom-test-class',
    },
    filterPanel: {
      logicOperators: [GridLogicOperator.Or],
      filterFormProps: {
        // Customize inputs by passing props
        logicOperatorInputProps: {
          variant: 'outlined',
          size: 'small',
        },
        columnInputProps: {
          variant: 'standard',
          size: 'small',
          sx: { mt: 'auto' },
        },
        operatorInputProps: {
          variant: 'standard',
          size: 'small',
          sx: { mt: 'auto' },
          className: 'custom-header-dropdown',
        },
        valueInputProps: {
          InputComponentProps: {
            variant: 'standard',
            size: 'small',
          },
        },
      },
    },
  }), [])

  const handleOnColumnOrderChange = useCallback((_a: any, _b: any, c: any) => {
    const updatedColList = c.api
      .getAllColumns()
      .slice(3)
      .map((col: any) => {
        const { name, label, dataType, UIDataType, fieldId, isSortable } =
          col;
        return { name, label, dataType, UIDataType, fieldId, isSortable };
      });
    dispatch(
      setColumnsList({ isNlpSearch, columns: updatedColList })
    );
  }, [dispatch, isNlpSearch])

  const handleItemPerPageChange = (itemPerPage: number) => {
    setItemsPerPage(itemPerPage);
    if (selectedItem !== SelectedItems.allInDocument) {
      setSelectionModel([]);
      setSelectedItem(null);
    }
  };

  const isRowSelectable = useCallback(
    () => selectedItemRef.current !== SelectedItems.allInDocument,
    []
  );

  const getRowClassName = useCallback(
    (params: { row: { id: string } }) =>
      expandedRowIds.includes(params.row.id) ? 'highlight-row' : '',
    [expandedRowIds]
  );

  const getDetailPanelContent = useCallback(
    ({ row }: { row: docListResponseType }) => (
      <DetailPanelContent row={row} handleRowExpansion={handleRowExpansion} />
    ),
    [handleRowExpansion]
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const onGridStateChange = useCallback((e: { columns?: { orderedFields?: string[] } }) => {
    if (e?.columns?.orderedFields) {
      columnsRef.current = e.columns.orderedFields;
    }
  }, []);

  const selectionHeaderContextValue = useMemo(
    () => ({
      selectedItem,
      selectionModel,
      rowsLength: rows.length,
      onHeaderCheckboxChange: handleSelectAllChange,
    }),
    [selectedItem, selectionModel, rows.length, handleSelectAllChange]
  );

  return (
    <Box className={'eca-doc-main-container'}>
      <Box className={'eca-doc-list-top'}>
        <>
          {searchTime && (
            <Box className="doc-list-banner">
              <Box className="icon-container">
                <InfoOutlinedIcon />
                <Typography className="typo-text label1_size info-text">
                  {t('common.aurEcaSearchLoader.docInfo').replace(
                    '{current}',
                    searchTime
                  )}
                </Typography>
              </Box>
              <Box>
                <AurButton
                  label={t('common.aurEcaSearchLoader.revertBtn')}
                  title={t('common.aurEcaSearchLoader.revertBtn')}
                  type="text"
                  size="Small"
                  classNames="info-text"
                  endIcon={<ReplayOutlinedIcon className="info-text" aria-hidden="true" />}
                  onClick={() => {
                    openDrawer && openDrawer(true);
                    trackGAEventRef.current({
                      eventName: GA_EVENT_NAMES.CLICK,
                      click: {
                        name: t(
                          'gaTracking.ecaDocList.clearSearchQueryAndFilters'
                        ),
                        type: GA_CLICK_TYPES.LINK,
                        region: GA_REGIONS.BODY,
                        action: t(
                          'gaTracking.ecaDocList.clearSearchQueryAndFiltersBegin'
                        ),
                      },
                    });
                    trackGAEventRef.current({
                      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                      subPage: {
                        subpage_name: t(
                          'gaTracking.ecaDocList.clearSearchQueryAndFiltersSubPage'
                        ),
                        subpage_type: GA_SUBPAGE_TYPE.MODAL,
                      },
                    });
                  }}
                />
              </Box>
            </Box>
          )}
          <Box className="eca-document-list-container">
            <Box className="text-container">
              <Typography
                className="typo-text subhead2_size custom-color"
                tabIndex={0}
              >
                {t('ecaDocList.documentList')}
              </Typography>
              <Typography
                className="typo-text body2_size custom-color-for-doc-count"
                tabIndex={0}
              >
                {t('ecaDocList.docs', {
                  documentCount: formateNumber(allDocumentsCount),
                })}
              </Typography>
              {filterCount > 0 && (
                <div>
                  {' '}
                  <Chip
                    label={
                      <Typography
                        className="typo-text label-main"
                        tabIndex={0}
                      >
                        {t('ecaDocList.filters.filterApplied')}

                        {
                          <span className="typo-text label-number">
                            {` (`}
                            {filterCount}
                            {`)`}
                          </span>
                        }
                      </Typography>
                    }
                    className="filter-count-badge"
                    size="small"
                  />
                </div>
              )}
            </Box>
            {hasSavedSearchesPerm && setOpenSaveSearch && (
              <AurButton
                classNames="save-button"
                onClick={saveSearchBtnClick}
                type="continue"
                size="Medium"
                disabled={isDisabledSaveSearchBtn || saveSearchLoader}
                dataTestId="save-button"
                label={t('ecaDocList.saveSearchButton')}
                ariaLabel={t('ecaDocList.saveSearchButton')}
                showLoader={saveSearchLoader}
                showLabelWithLoader={true}
              />
            )}
          </Box>
          <Box className="eca-grid-toolbar">
            {rows.length > 0 ||
              (rows.length === 0 && filterCount > 0) ||
              loader ||
              runFlag.current ? (
              <AurPaginationPro
                showViewButton={hasDocumentPreviewPerm}
                showTagButton={hasTaggingDocumentPerm && hasSearchesPerm}
                isFilterApplied={filterCount > 0}
                onClearFilter={() => {
                  setClearFilterModal(true);
                }}
                totalItems={allDocumentsCount}
                selectedDocs={
                  selectedItem === SelectedItems.allInDocument
                    ? allDocumentsCount
                    : selectionModel.length
                }
                itemsPerPage={itemsPerPage}
                startIndex={startIndex}
                onPageChange={handlePageChange}
                setItemsPerPage={handleItemPerPageChange}
                handleAddSortFieldClick={() => {
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.CLICK,
                    click: {
                      name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
                      type: GA_CLICK_TYPES.ICON,
                      region: GA_REGIONS.BODY,
                      action: t(
                        'gaTracking.ecaDocList.drawerManageColumsSortOrder'
                      ),
                    },
                  });
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                    subPage: {
                      subpage_name: t(
                        'gaTracking.ecaDocList.manageColumsSortOrder'
                      ),
                      subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                    },
                  });
                  setShowDrawer(true);
                }}
                handleTagClick={handleTagClick}
                handleViewClick={handleViewClick}
                handleDownloadClick={handleDownloadClick}
                backgroundColor="#0c1119"
                selectionText={
                  selectedItem === SelectedItems.allInDocument
                    ? t('common.pagination.allSelectedDocs', {
                      selectedDocs: formateNumber(allDocumentsCount),
                    })
                    : t('common.pagination.selectedDocs', {
                      selectedDocs: formateNumber(selectionModel.length),
                    })
                }
              />
            ) : (
              <></>
            )}
          </Box>
        </>
      </Box>

      <Box className="eca-doclist-main" ref={mainScrollRef}>
        {rows.length > 0 ||
          (rows.length === 0 && filterCount > 0) ||
          loader ||
          runFlag.current ? (
          <Box className="eca-doclist-main-container">
            {loader ? <CustomLoadingOverlay /> : ''}

            <ThemeProvider theme={lightTheme}>
              <CssBaseline />
              <Box className="eca-doclist-container">
                <SelectionHeaderContext.Provider value={selectionHeaderContextValue}>
                  <DataGridPro
                    apiRef={apiRef}
                    onStateChange={onGridStateChange}
                    data-testid="eca-doc-list-table"
                    rows={rows}
                    columns={updatedColumns}
                    className="eca-doc-list-grid"
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: itemsPerPage,
                        },
                      },
                    }}
                    keepNonExistentRowsSelected
                    checkboxSelection
                    rowSelectionModel={selectionModel} // Set selection model
                    onRowSelectionModelChange={handleonRowSelectionModelChange}
                    isRowSelectable={isRowSelectable}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    headerFilters
                    hideFooter
                    columnHeaderHeight={48}
                    headerFilterHeight={48}
                    rowHeight={36}
                    getDetailPanelContent={getDetailPanelContent}
                    detailPanelExpandedRowIds={expandedRowIds}
                    getDetailPanelHeight={getDetailPanelHeight}
                    slots={gridSlots}
                    slotProps={gridSlotProps}
                    onColumnOrderChange={handleOnColumnOrderChange}
                    getRowClassName={getRowClassName}
                    filterMode="server"
                    filterModel={filterList}
                    onFilterModelChange={(e) => {
                      dispatch(setFilterList(e));
                    }}
                    disableColumnFilter
                  // disableVirtualization // This line rendering component multiple times and creating following issue ARHD-12679
                  />
                </SelectionHeaderContext.Provider>
              </Box>
            </ThemeProvider>
          </Box>
        ) : (
          <CustomNoRowsOverlay />
        )}
      </Box>
      {openSaveSearch && (
        <EcaSaveSearch
          setShowDrawer={() => setOpenSaveSearch?.(false)}
          showDrawer={openSaveSearch}
          isPublic={true}
          searchName=""
          filterSet={filterSet}
          onCancelClick={onCancelClick}
        />
      )}
      {showDrawer && (
        <FieldsSortDrawer
          showDrawer={showDrawer}
          handleDrawer={setShowDrawer}
          currentColumns={pageColumns}
          sortOrder={pageSortOrder}
          isNlpSearch={isNlpSearch}
          clientId={clientId ?? ''}
          projectId={projectId ?? ''}
        />
      )}

      {clearFilterModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={clearFilterModal}
          onOk={handleClearFilters}
          onCancel={() => setClearFilterModal(false)}
          showPopupHeading={true}
          buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
          showPopupTitle={true}
          showCloseIcon={true}
          popupTitle={t('ecaDocList.filters.popupTitle')}
          popupHeading={t('ecaDocList.filters.popupHeading')}
          labelActionCancel={t('buttons.cancel')}
          labelActionOk={t('ecaDocList.filters.popupOk')}
        />
      )}
      {showFamilyMembersWarningModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={showFamilyMembersWarningModal}
          onOk={confirmationModalOkHandler}
          onCancel={confirmationModalCloseHandler}
          onClose={confirmationModalCloseHandler}
          popupHeading={t(
            'indexSearchPage.familyMembersWarningModal.popupHeading'
          )}
          popupTitle={t('indexSearchPage.familyMembersWarningModal.popupTitle')}
          popupContent={t(
            'indexSearchPage.familyMembersWarningModal.popupContent'
          )}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t(
            'indexSearchPage.familyMembersWarningModal.buttonCancel'
          )}
          cancelButtonTitle=""
          labelActionOk={t(
            'indexSearchPage.familyMembersWarningModal.buttonYes'
          )}
        />
      )}
      <BulkTaggingModal
        ref={bulkTaggingRef}
        showModal={showBulkTaggingModalVisible}
        indexName={title}
        handleModal={setShowBulkTaggingModal}
        selectdDocumentIds={selectedDocumentIds}
        allDocumentsSelected={selectedItem === SelectedItems.allInDocument}
        allDocumentsCount={
          selectedItem === SelectedItems.allInDocument
            ? allDocumentsCount
            : selectionModel.length
        }
        searchId={searchID ?? ''}
        filterset={filterSet}
        flowType={TaggingFLowType.ECA}
      />
      {ecaDocumentViewer ? (
        <div data-testId="document-viewer">
          <EcaDocumentContainer
            selectedDocumentIds={selectionDocuments}
            selectedDocLink={selectedDocLink}
            docsPerPage={itemsPerPage}
            docsCurrentPage={startIndex}
            scrollFetching={scrollFetching}
            searchID={searchID}
            filterSet={filterSet}
            indexName={title}
            isNlpSearch={isNlpSearch}
            stdIndexFieldTemplateId={stdIndexFieldTemplateId}
          />
        </div>
      ) : null}
      {showSearchLoader && (
        <AurEcaSearchLoader
          isOpen={showSearchLoader}
          onClose={handleLoaderClose}
          keepMounted
          isExportDoc={true}
        />
      )}
    </Box>
  );
});

const arePropsEqual = (
  prevProps: {
    searchID?: string;
    searchTime?: string;
    openSaveSearch?: boolean;
    viewDocType?: string;
    isNlpSearch?: boolean;
    saveSearchLoader?: boolean;
    runSearchError?: IRunSearchError;
  },
  nextProps: {
    searchID?: string;
    searchTime?: string;
    openSaveSearch?: boolean;
    viewDocType?: string;
    isNlpSearch?: boolean;
    saveSearchLoader?: boolean;
    runSearchError?: IRunSearchError;
  }
) => {
  // Only re-render if the searchID or searchTime change
  return (
    prevProps.searchID === nextProps.searchID &&
    prevProps.searchTime === nextProps.searchTime &&
    prevProps.openSaveSearch === nextProps.openSaveSearch &&
    prevProps.viewDocType === nextProps.viewDocType &&
    prevProps.isNlpSearch === nextProps.isNlpSearch &&
    prevProps.saveSearchLoader === nextProps.saveSearchLoader &&
    prevProps.runSearchError === nextProps.runSearchError
  );
};

export default React.memo(EcaDocListGrid, arePropsEqual);
